import React, { useEffect, useState } from 'react';
import { Wifi, WifiOff, RefreshCw, CloudCheck, AlertTriangle } from 'lucide-react';
import { checkServerConnection } from '../lib/offline/connectivity';
import { processSyncQueue } from '../lib/offline/syncManager';
import { getPendingOperations } from '../lib/offline/db';

export const ConnectionStatus: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [serverReachable, setServerReachable] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const checkStatus = async () => {
    const online = navigator.onLine;
    setIsOnline(online);
    if (online) {
      const reachable = await checkServerConnection();
      setServerReachable(reachable);
      if (reachable) {
        const ops = await getPendingOperations();
        const pending = ops.filter(o => o.syncStatus === 'pending' || o.syncStatus === 'failed');
        setPendingCount(pending.length);
        if (pending.length > 0 && !syncing) {
          setSyncing(true);
          await processSyncQueue();
          const updatedOps = await getPendingOperations();
          setPendingCount(updatedOps.filter(o => o.syncStatus === 'pending' || o.syncStatus === 'failed').length);
          setSyncing(false);
        }
      }
    } else {
      setServerReachable(false);
      const ops = await getPendingOperations();
      setPendingCount(ops.filter(o => o.syncStatus === 'pending' || o.syncStatus === 'failed').length);
    }
  };

  useEffect(() => {
    window.addEventListener('online', checkStatus);
    window.addEventListener('offline', checkStatus);
    checkStatus();

    const interval = setInterval(checkStatus, 30000);
    return () => {
      window.removeEventListener('online', checkStatus);
      window.removeEventListener('offline', checkStatus);
      clearInterval(interval);
    };
  }, []);

  const handleManualSync = async () => {
    setSyncing(true);
    await processSyncQueue();
    await checkStatus();
    setSyncing(false);
  };

  if (isOnline && serverReachable && pendingCount === 0) {
    return (
      <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold border border-emerald-200">
        <Wifi className="w-3.5 h-3.5" />
        <span>Online</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-800 rounded-full text-xs font-semibold border border-amber-200 shadow-sm">
      {!isOnline || !serverReachable ? (
        <>
          <WifiOff className="w-3.5 h-3.5 text-amber-600 animate-pulse" />
          <span>Offline Mode</span>
        </>
      ) : (
        <>
          <RefreshCw className={`w-3.5 h-3.5 text-blue-600 ${syncing ? 'animate-spin' : ''}`} />
          <span>Synchronising</span>
        </>
      )}
      {pendingCount > 0 && (
        <span className="bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded-full text-[10px] font-bold">
          {pendingCount} pending
        </span>
      )}
      <button 
        onClick={handleManualSync}
        disabled={syncing}
        className="ml-1 underline hover:text-amber-900 cursor-pointer disabled:opacity-50"
      >
        Sync Now
      </button>
    </div>
  );
};
